import { SectionHeader } from '../common/section-header'
import { CALL_HISTORY_NAVIGATION } from '../navigation/admin-navigation'
import { CompanyInformationForm } from './company-information-form'
import { personLabel } from '../common/person-label.js'
import {
  FormalNameFields,
  StudentAddressFields,
} from '../common/identity-fields.js'
/**
 * Edit-student page.
 *
 * A deep-linkable route (/admin/students/:userId/edit) rather than an
 * inline panel, so it works with a 195-row list, direct load, and
 * refresh. Loads the student via GET and keeps identity maintenance separate
 * from enrollment operations. English-only internal surface.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, buttonVariants, FieldHelp, Icon, Notice } from '@speaking-coach/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { keysStaleAfterStudentChange, queryKeys } from '../api/query-keys'
import { Link, useParams } from 'react-router-dom'
import { InternalBadge } from '../cohorts/cohort-form-fields'
import { adminStudentClient, messageForStudentError } from './api'
import { failureReasonProse } from './invite-state-cell'
import { PendingSpinner } from '../common/save-button'
import { SaveButton } from '../common/save-button'
import { UnsavedChangesGuard } from '../common/unsaved-changes'
import { UndeliverableNotice } from '../email_suppressions/undeliverable-notice'
import { editStudentSchema, type EditStudentForm } from './student-schema'
import type { StudentRow } from './types'
import { EnrollmentOperationsSection } from './enrollment-operations-section'
import { HistoryLink } from '../activity/history-link'
import { PageHeader } from '../components/page-header'
import { OPERATOR_DETAIL_PAGE, OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { formatBusinessDate, todayJstIso } from '../common/access-window'
import { StudentStatusPanel } from './student-status'

type LoadStatus = 'loading' | 'loaded' | 'error'

/**
 * Where this student's `isInternal` came from, in words.
 *
 * Shown for BOTH values, not just `internal`, because it determines analytics
 * visibility even though it no longer grants any credential-setting control.
 *
 * Deliberately NOT rendered as a live `client → cohort → student` chain. The
 * classification is COPY-ON-CREATE: it is stamped onto the student at creation
 * and a later edit to the cohort or the client does not move them. A chain
 * would imply "change the cohort and this follows", which is false, and an
 * operator acting on that would quietly leave data mis-classified. So the
 * student's own value is stated as the answer and the source named as history.
 *
 * Precedence is `cohort ?? client` — the cohort wins, and an explicit
 * customer cohort inside an internal client beats the client default. `null`
 * on the cohort means "no cohort, or no opinion", which fall through alike.
 */
function classificationOrigin(student: StudentRow): string {
  const value = student.isInternal ? 'Internal' : 'Customer'
  const fromCohort = student.cohortIsInternal !== null
  const expected = fromCohort
    ? student.cohortIsInternal
    : student.clientIsInternal
  // Disagreement is not a defect — it is what copy-on-create produces once a
  // cohort is edited or a student is moved between cohorts. Saying so is the
  // only way an operator can answer "why is this one classified differently
  // from its cohort?", and silence would read as a bug in the page.
  if (expected !== student.isInternal) {
    return `${value} — stamped at creation. ${
      fromCohort ? 'Their cohort' : 'The client'
    } now says ${expected ? 'internal' : 'customer'}; classification does not follow later changes.`
  }
  return `${value} — from ${fromCohort ? 'their cohort' : 'the client default'} at creation.`
}

/**
 * Invite states in which a send has NOT happened, so its schedule can still
 * be moved or canceled. Everything else has a token out there (or had one),
 * and the honest action is Resend — the server refuses a reschedule with
 * 409 `invite_already_sent`, which is what this list exists to pre-empt.
 */
/**
 * One line of invite narration that is either ordinary output or a
 * warning. The gray path stays a plain paragraph; the warning path is the
 * shared `Notice`, so "this student receives nothing" wears the same box
 * everywhere in the app.
 */
function StateLine({
  warning,
  children,
}: {
  warning: boolean
  children: ReactNode
}): JSX.Element {
  if (!warning) return <p className="text-text-secondary text-body">{children}</p>
  return <Notice variant="warning">{children}</Notice>
}

/**
 * Explain a known enrollment-side refusal before the operator tries to send.
 *
 * This mirrors only facts already present in the student detail DTO. The
 * delivery service remains authoritative and rechecks the exact locked
 * enrollment when the command runs. A missing enrollment is itself a known
 * refusal after cutover; there is no identity-owned activation fallback.
 */
function activationEnrollmentRefusal(student: StudentRow): string | null {
  const status = student.enrollmentStatus
  if (status === null) {
    return 'This student has no enrollment — create an enrollment before sending an activation email.'
  }

  const today = todayJstIso()
  const startsOn = student.resolvedScheduledStartOn
  const endsOn = student.programAccessEndsOn

  if (status === 'ended') {
    return endsOn !== null && endsOn < today
      ? `Program access ended ${formatBusinessDate(endsOn)} — create a new enrollment before sending an activation email.`
      : 'This enrollment has ended — create a new enrollment before sending an activation email.'
  }
  if (status === 'cancelled') {
    return 'This enrollment was cancelled — create a new enrollment before sending an activation email.'
  }
  // A suspended current enrollment may be the operator projection while a
  // separate future enrollment owns the latest recoverable delivery. The DTO
  // does not expose that delivery's enrollment, so blocking here could hide a
  // valid resend. The command locks and validates the exact delivery instead.
  if (status === 'suspended') {
    return null
  }
  if (status === 'draft') {
    return 'This enrollment is still a draft — cancel and reschedule it with complete access dates before sending an activation email.'
  }
  if (endsOn !== null && endsOn < today) {
    return `Program access ended ${formatBusinessDate(endsOn)} — create a new enrollment before sending an activation email.`
  }
  if (startsOn === null || endsOn === null) {
    return status === 'pending'
      ? 'This enrollment has incomplete access dates — cancel and reschedule it with complete dates before sending an activation email.'
      : 'This enrollment has incomplete access dates — end and replace it with complete dates before sending an activation email.'
  }

  // First activation is allowed before access starts. A pending future period
  // is therefore as deliverable as an active one; only the inclusive end date
  // closes the window.
  return null
}

/**
 * Where the student is in the invite lifecycle, and the actions on it.
 *
 * The state is DERIVED server-side (ADR-0052 D8d) — one function feeds this
 * page, the roster and the later cohort funnel, so they cannot disagree
 * about whether a link is outstanding.
 *
 * Two states need an operator, for opposite reasons. `expired` means the
 * invite was sent and never used — a different problem from `pending`, where
 * waiting is still right, which is why they are not collapsed into "not
 * activated". `failed` means nothing was delivered at all: a live token
 * exists, so deriving from the token alone would call this `pending` and
 * leave the operator waiting on someone holding nothing.
 */
function InviteSection({ student }: { student: StudentRow }): JSX.Element {
  const queryClient = useQueryClient()
  const [done, setDone] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const scheduledDeliveryDue =
    student.inviteState === 'scheduled' &&
    student.inviteScheduledFor !== null &&
    Date.parse(student.inviteScheduledFor) <= now

  useEffect(() => {
    if (
      student.inviteState !== 'scheduled' ||
      student.inviteScheduledFor === null
    ) {
      return
    }
    const scheduledFor = Date.parse(student.inviteScheduledFor)
    if (!Number.isFinite(scheduledFor)) return
    const currentTime = Date.now()
    if (scheduledFor <= currentTime) {
      // The refetch can cross the due instant between render and this effect.
      // In that case `now` still predates the delivery even though the live
      // clock does not. Advance the captured clock once instead of leaving
      // "Sends" stale until some unrelated render.
      if (now < scheduledFor) setNow(currentTime)
      return
    }

    // Keep the wording honest when the operator leaves this page open across
    // the scheduled minute. Browsers clamp very long timeouts, so wake at
    // most every ~24 days and schedule the remainder on the next render.
    const maxTimeoutMs = 2_147_000_000
    const timeout = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(scheduledFor - currentTime + 100, maxTimeoutMs),
    )
    return () => window.clearTimeout(timeout)
  }, [student.inviteScheduledFor, student.inviteState, now])

  function invalidate(): void {
    for (const key of keysStaleAfterStudentChange(student.clientId)) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
    // The invite endpoints answer with the action's outcome, not the whole
    // row, so the detail cannot be seeded from the response and has to be
    // refetched — otherwise this page keeps showing the old state.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.student(student.userId),
      exact: true,
    })
  }

  const resend = useMutation({
    mutationFn: () => adminStudentClient.resendInvite(student.userId),
    onSuccess: () => {
      const replacesWorkingLink = student.inviteState === 'pending'
      setDone(
        replacesWorkingLink
          ? 'Replacement invitation queued for delivery. The previous link no longer works.'
          : 'Invitation queued for delivery.',
      )
      invalidate()
    },
  })

  const busy = resend.isPending
  const error =
    resend.error !== null ? messageForStudentError(resend.error) : null

  // Activation belongs to the identity, while its delivery belongs to one
  // exact enrollment window. Identity recovery wins as the explanation once
  // credentials exist; otherwise pre-empt only enrollment refusals the DTO can
  // prove, leaving the server to recheck under the write lock.
  const refusal =
    student.inviteState === 'activated'
      ? 'This student already has account credentials. If they cannot sign in, have them use the sign-in recovery link.'
      : activationEnrollmentRefusal(student)

  return (
    <section
      aria-label="Account access"
      className="bg-surface-card rounded-token-card p-6 space-y-3"
    >
      <div>
        <SectionHeader title="Account access" className="mb-1" />
        {/* The attention states — the same three the roster badges as
            `attention` — read as a WARNING (shared Notice), not another
            gray line. Each means this student receives nothing and does
            not know it: `suppressed` deliberately (until restored, they
            are never invited), `failed` and `overdue` by defect. */}
        <StateLine
          warning={
            student.inviteState === 'suppressed' ||
            student.inviteState === 'failed' ||
            student.inviteState === 'overdue'
          }
        >
          {scheduledDeliveryDue
            ? 'Invitation is due and queued for delivery. It will go out when the delivery worker runs.'
            : INVITE_STATE_LABEL[student.inviteState]}
          {/* Preserve a known delivery failure as diagnostic history even
              when enrollment eligibility currently hides the retry. Generic
              retry advice only appears when the action is actually offered. */}
          {student.inviteState === 'failed' &&
            (student.inviteSendFailedReason !== null || refusal === null) && (
            <>
              {' '}
              {student.inviteSendFailedReason !== null
                ? failureReasonProse(student.inviteSendFailedReason)
                : 'Check the address, then resend.'}
            </>
          )}
          {student.inviteSentAt !== null && (
            <> · Sent {formatJstDate(student.inviteSentAt)}</>
          )}
          {/* The support answer to "my link doesn't work": each send
              invalidates the previous one, so a student who received
              several emails is probably clicking an old one. */}
          {(student.inviteSendCount ?? 0) >= 2 &&
            (student.inviteState === 'pending' ||
              student.inviteState === 'expired' ||
              student.inviteState === 'failed') && (
              <>
                {' '}· {student.inviteSendCount} invitations sent — only the
                newest link works
              </>
            )}
          {student.inviteState === 'pending' && student.inviteExpiresAt !== null && (
            <> · Expires {formatJstDate(student.inviteExpiresAt)}</>
          )}
          {(student.inviteState === 'scheduled' ||
            student.inviteState === 'overdue') &&
            student.inviteScheduledFor !== null && (
              <>
                {' '}·{' '}
                {scheduledDeliveryDue || student.inviteState === 'overdue'
                  ? 'Due'
                  : 'Sends'}{' '}
                {formatJstDateTime(student.inviteScheduledFor)}
              </>
            )}
          {student.inviteState === 'scheduled' &&
            student.inviteSendAtOverride !== null && (
              <> · Individual date, not their cohort&apos;s</>
            )}
          {student.inviteSuppressedAt !== null &&
            student.inviteState === 'suppressed' && (
              <> · Canceled {formatJstDate(student.inviteSuppressedAt)}</>
            )}
          {student.activatedAt !== null && (
            <> · Activated {formatJstDate(student.activatedAt)}</>
          )}
        </StateLine>
      </div>

      {refusal !== null && <StateLine warning={false}>{refusal}</StateLine>}

      {/* An overdue send is not an operator mistake, so the message points at
          the cause rather than at them. It means the hourly sweep did not
          run: a dead cron emits no errors and no email, so this row is the
          only place the silence becomes visible. */}
      {student.inviteState === 'overdue' && refusal === null && (
        <p className="text-text-secondary text-body">
          This send was due and did not happen — the scheduled-send worker may
          not be running. Check the worker, then requeue this invitation if it
          still needs delivery.
        </p>
      )}

      {/* Said BEFORE the click, not in the success message afterwards.
          Resend deliberately has no confirmation dialog — unlike Deactivate
          enrollment, it is self-healing (the student ends up holding a working
          link either way) and it is the highest-volume action here, so a
          modal on the common path would cost a click every time and devalue
          the one on Deactivate. What a modal could not tell the operator is
          the thing that actually matters: whether this student is
          mid-activation right now. So state the consequence instead, and
          only when there IS a live link to invalidate — in `expired` /
          `failed` / `none` nothing is lost. */}
      {student.inviteState === 'expired' && refusal === null && (
        <p className="text-text-secondary text-body">
          They can request a new one from the sign-in page, or resend it
          here.
        </p>
      )}

      {student.inviteState === 'pending' && refusal === null && (
        <p className="text-text-secondary text-body">
          They already have a working link. Resending replaces it, so the one
          in their inbox stops working — including if they are part-way through
          setting their password.
        </p>
      )}

      {error !== null && (
        <div
          role="alert"
          className="bg-surface-app border border-danger/40 p-3 rounded-token-card text-body"
        >
          {error}
        </div>
      )}
      {done !== null && (
        <p role="status" className="text-text-secondary text-body">
          {done}
        </p>
      )}

      {/* Activated identities use account recovery. Known closed/ineligible
          enrollment windows explain the repair instead of offering a button
          the server will refuse. Scheduled invitations already have queued
          work, so another action would only replace the token and add a
          duplicate delivery. */}
      {refusal === null && student.inviteState !== 'scheduled' && (
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={() => {
              setDone(null)
              resend.mutate()
            }}
            disabled={busy}
            pending={resend.isPending}
            variant="secondary"
          >
            {/* "Resend" implies provider acceptance, while a failed attempt is
                a retry and a never-attempted invitation is a first send. Keep
                those operator actions distinct even though they share one
                enrollment-owned delivery endpoint. */}
            {resend.isPending ? (
              <>
                <PendingSpinner surface="card" />
                Sending…
              </>
            ) : (
              <>
                <Icon
                  name={
                    student.inviteState === 'failed' ||
                    student.inviteState === 'overdue' ||
                    student.inviteSentAt !== null
                      ? 'resend'
                      : 'send'
                  }
                />
                {student.inviteState === 'failed'
                  ? 'Retry activation email'
                  : student.inviteState === 'overdue'
                    ? 'Requeue activation email'
                    : student.inviteSentAt === null
                      ? 'Send activation email'
                      : 'Resend activation email'}
              </>
            )}
          </Button>
        </div>
      )}
    </section>
  )
}

const INVITE_STATE_LABEL: Record<StudentRow['inviteState'], string> = {
  none: 'No invitation sent yet.',
  // Distinct from `none` on purpose: this student was scheduled, not
  // forgotten. Collapsing the two is how an operator resends a whole wave
  // by hand and defeats the schedule they set.
  scheduled: 'Invitation scheduled — not sent yet. It goes out automatically on its date.',
  // Distinct from BOTH: something was planned and it did not happen, which
  // is a worker problem rather than a student problem.
  overdue: 'Invitation was due and has not gone out.',
  suppressed: 'Scheduled send canceled. They have not been invited, and will not be until you restore it or resend.',
  pending: 'Invitation sent — waiting for them to set a password.',
  // NOT "sent": nothing reached them. A live token exists, which is exactly
  // why this cannot be left reading `pending`. The next-move advice is
  // appended in the JSX: the server's failure REASON when it knows one
  // (PROV-ALERTS), the generic "check the address" only when it does not.
  failed: 'Invitation could not be emailed — they have received nothing.',
  // Fact only — the old label appended "request a new one from the
  // sign-in page, or resend it here". The advice renders conditionally,
  // below.
  expired: 'Invitation expired unused.',
  activated: 'Account active — credentials already exist.',
}

export function EditStudentPage(): JSX.Element {
  const { userId = '' } = useParams()
  const queryClient = useQueryClient()
  const studentQuery = useQuery({
    queryKey: queryKeys.student(userId),
    queryFn: () => adminStudentClient.get(userId),
  })

  const student = studentQuery.data ?? null
  const status: LoadStatus = studentQuery.isPending
    ? 'loading'
    : studentQuery.isError
      ? 'error'
      : 'loaded'
  const loadError =
    studentQuery.error !== null ? messageForStudentError(studentQuery.error) : null

  // The identity PATCH returns the updated row, so seed this detail without
  // an extra round trip. Enrollment mutations own their separate query.
  const setStudent = (row: StudentRow): void => {
    queryClient.setQueryData(queryKeys.student(userId), row)
  }

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_DETAIL_PAGE}>
        {status === 'loading' && (
          <p className="text-text-secondary mt-4">Loading student…</p>
        )}
        {status === 'error' && (
          <div role="alert" className="mt-4 bg-surface-card border border-danger/40 p-4 rounded-token-card">
            <p className="font-medium">Could not load student</p>
            <p className="text-text-secondary text-body mt-1">{loadError}</p>
          </div>
        )}
        {status === 'loaded' && student && (
          <EditForm student={student} onChange={setStudent} />
        )}
      </div>
    </main>
  )
}

interface EditFormProps {
  student: StudentRow
  onChange: (row: StudentRow) => void
}

/** Only identity fields belong in this form. */
function editFormValues(student: StudentRow): EditStudentForm {
  return {
    firstName: student.firstName ?? '',
    lastName: student.lastName ?? '',
    username: student.username ?? '',
    addressSuffix: student.addressSuffix ?? 'san',
  }
}

function EditForm({ student, onChange }: EditFormProps): JSX.Element {
  const [companyDirty, setCompanyDirty] = useState(false)
  const queryClient = useQueryClient()

  // zod + RHF (ADMIN-FORMS). `isDirty` drives both the Save gate and the
  // unsaved-changes guard.
  const form = useForm<EditStudentForm>({
    resolver: zodResolver(editStudentSchema),
    mode: 'onSubmit',
    defaultValues: editFormValues(student),
  })
  const { handleSubmit, formState } = form

  const save = useMutation({
    mutationFn: (values: EditStudentForm) =>
      adminStudentClient.update(student.userId, {
        ...values,
        identityVersion: student.identityVersion ?? 0,
      }),
    onSuccess: (updated) => {
      onChange(updated)
      // Reset to the FRESH row, not to the old defaults: this is what
      // clears `isDirty`, which re-disables Save and puts "Saved ✓" on the
      // button. Without it the form would offer to re-save what it just
      // saved, forever.
      form.reset(editFormValues(updated))
      // The name appears in every roster, so those projections are stale.
      for (const key of keysStaleAfterStudentChange(student.clientId)) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  const saving = save.isPending
  const saveError = save.error !== null ? messageForStudentError(save.error) : null
  const onValid = (values: EditStudentForm): void => {
    // The button is disabled for a clean form too, but Enter in a text
    // field submits past a disabled button.
    if (!formState.isDirty || saving || student.identityConfirmedAt) return
    save.mutate(values)
  }

  return (
    <section aria-label={`Student ${personLabel(student) ?? 'Student'}`} className="mt-4 space-y-4">
      <PageHeader
        title={personLabel(student) ?? 'Student'}
        meta={
          student.isInternal ? (
            <span
              className="font-normal"
              title="Internal: excluded from customer analytics. Not an access flag."
            >
              <InternalBadge />
            </span>
          ) : undefined
        }
        action={
          <div className="flex gap-2">
            {student.callHistoryCount !== 0 && (
              <Link className={buttonVariants({ variant: 'secondary', size: 'sm' })} to={`/admin/sessions?userId=${student.userId}`}>
                <Icon name={CALL_HISTORY_NAVIGATION.icon} />
                Call history
                {student.callHistoryCount != null && (
                  <span className="rounded-full bg-surface-app px-1.5 text-label tabular-nums">{student.callHistoryCount}</span>
                )}
              </Link>
            )}
            <HistoryLink to={`/admin/students/${student.userId}/history`} />
          </div>
        }
      />

      <StudentStatusPanel
        student={student}
        createdLabel={formatJstDate(student.createdAt)}
        lastPracticeLabel={formatJstDate(
          student.lastActiveAt,
          'No practice recorded',
        )}
        notice={
          student.emailUndeliverable != null ? (
            <UndeliverableNotice value={student.emailUndeliverable} />
          ) : undefined
        }
      />

      {/* ADR-0068 participation periods are the only participation controls on
          this page, so they lead the operational hierarchy. Cohort membership
          and access-ending actions belong to an enrollment; the old
          participation controls would bypass its locked terms. */}
      <EnrollmentOperationsSection student={student} />

      {/* Account activation/recovery is important but supports participation;
          it does not define it. Keeping this below enrollment prevents an
          identity credential from reading as the student's current program. */}
      <InviteSection student={student} />

      {/* Navigating away from unsaved edits asks first. Wired to the same
          `isDirty` that gates Save, so the two cannot disagree about
          whether there is anything to lose. */}
      <UnsavedChangesGuard
        when={formState.isDirty || companyDirty}
        name={`student ${student.email}`}
      />

      <form
        onSubmit={handleSubmit(onValid)}
        aria-label="Student profile"
        className="bg-surface-card rounded-token-card p-6 space-y-4"
        noValidate
      >
        <SectionHeader title="Student profile" description={"Formal names are used for company reporting and certificates. Preferred names and suffixes are private from the company."} />
        {/* `noValidate`: the zod schema is the authority — native
            constraint validation would pre-empt it with the browser's
            bubble, and in jsdom it blocks submission outright so
            `handleSubmit` never runs. */}
        <dl>
          <dt className="flex items-center gap-1 text-body text-text-secondary">
            Identity confirmation
            <FieldHelp label="identity confirmation">
              Shows whether the student has reviewed and confirmed their family
              name, given name, preferred name and coach suffix. After confirmation,
              only the student can change these fields; operators and imports
              cannot overwrite them.
            </FieldHelp>
          </dt>
          <dd className="mt-1 text-body">
            {student.identityConfirmedAt ? (
              <>
                Confirmed on{' '}
                <time dateTime={student.identityConfirmedAt}>
                  {formatJstDateTime(student.identityConfirmedAt)}
                </time>
              </>
            ) : (
              'Not confirmed'
            )}
          </dd>
        </dl>
        {student.identityConfirmedAt && (
          <Notice variant="neutral">
            The student has confirmed this identity and manages these fields in
            their Account.
          </Notice>
        )}
        <fieldset
          disabled={!!student.identityConfirmedAt}
          className="space-y-4"
        >
          <FormalNameFields
            firstName={form.register('firstName')}
            lastName={form.register('lastName')}
            errors={form.formState.errors}
          />
          <Controller
            control={form.control}
            name="addressSuffix"
            render={({ field }) => (
              <StudentAddressFields
                username={form.register('username')}
                required={false}
                suffix={field.value}
                onSuffixChange={field.onChange}
                errors={form.formState.errors}
              />
            )}
          />
        </fieldset>

        <dl className="border-t border-border pt-4 text-body">
          <div>
            <dt className="text-text-secondary">Data classification</dt>
            <dd>
              {classificationOrigin(student)}
              <span className="mt-1 block text-caption text-text-secondary">
                Analytics classification only; it never grants account or
                enrollment access.
              </span>
            </dd>
          </div>
        </dl>

        {saveError && (
          <div role="alert" className="bg-surface-app border border-danger/40 p-3 rounded-token-card text-body">
            {saveError}
          </div>
        )}

        {/* The confirmation lives ON the button ("Saved ✓"), not in an
            inline line that pushes the layout down when it appears. Clean
            form → disabled: re-saving an unchanged row is never what an
            operator means. */}
        <SaveButton
          pending={saving}
          dirty={formState.isDirty && !student.identityConfirmedAt}
          justSaved={save.isSuccess}
        />
      </form>
      <CompanyInformationForm student={student} onChange={onChange} onDirty={setCompanyDirty} />
    </section>
  )
}

/**
 * An INSTANT (`timestamptz`) rendered as the calendar day it falls on **in
 * Japan**, because that is where the operators are.
 *
 * The distinction from `formatBusinessDate` is not cosmetic. Formatting an
 * instant in UTC is wrong for the nine hours between midnight and 09:00
 * JST — an invite sent at 02:00 on the 6th displays as the 5th. That is
 * every early-morning action an operator takes, not an edge case.
 *
 * Asia/Tokyo is hardcoded rather than read from the browser deliberately:
 * two operators in different places must read the same roster the same way,
 * and the program dates these sit beside are already Japan business
 * dates (`cohorts.scheduled_start_on`, the 09:00 JST send convention).
 */
function formatJstDate(value: string | null, emptyLabel = 'not set'): string {
  if (!value) return emptyLabel
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Tokyo',
  }).format(new Date(value))
}

/**
 * An instant with its time, in JST — for a FUTURE commitment where the hour
 * matters.
 *
 * A cohort's send schedule resolves to 09:00 JST for the offset shape, but a
 * fixed `invite_send_at` is whatever timestamp an operator entered, and
 * date-only would hide it. The zone is named in the output because an
 * unlabeled "09:00" beside a UTC-stored value is exactly the ambiguity this
 * pair of functions exists to remove.
 */
function formatJstDateTime(value: string | null, emptyLabel = 'not set'): string {
  if (!value) return emptyLabel
  const formatted = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Tokyo',
  }).format(new Date(value))
  return `${formatted} JST`
}
